import {
  Box,
  Center,
  Grid,
  HStack,
  Link,
  Text,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { RankChangeIndicator } from '@/components';
import { AI_LEAGUE_NAME_COLOR } from '@/constants/aiLeague/aiLeagueBrand';
import type { AiModelId } from '@/constants/aiLeague/aiLeagueTypes';
import type { AiLeagueLeaderboardEntry } from '@/hooks/useAiLeaguePageData';
import type { leagueMember } from '@/models/user';
import {
  LEADERBOARD_ROW_COLUMNS,
  LEADERBOARD_ROW_COLUMNS_COMPACT,
  LEADERBOARD_ROW_MIN_H,
  LEADERBOARD_ROW_PX,
  LEADERBOARD_ROW_PY,
  LEADERBOARD_YOU_BORDER,
  LEADERBOARD_YOU_BORDER_COLOR,
} from '@/views/LeaguePage/LeaderboardRow';

export function AiLeagueLeaderboardRow(props: {
  entry: AiLeagueLeaderboardEntry;
  member: leagueMember;
  listPosition: number;
  tournamentStarted: boolean;
  onModelClick?: (modelId: AiModelId) => void;
}) {
  const { entry, member, listPosition, tournamentStarted, onModelClick } = props;
  const isYou = entry.id === 'you';
  const isModel = entry.id !== 'you';
  const aiNameColor = useColorModeValue(AI_LEAGUE_NAME_COLOR, 'white');
  const columns =
    useBreakpointValue({
      base: LEADERBOARD_ROW_COLUMNS_COMPACT,
      sm: LEADERBOARD_ROW_COLUMNS,
    }) ?? LEADERBOARD_ROW_COLUMNS_COMPACT;
  const rowPx = useBreakpointValue({ base: 2, sm: LEADERBOARD_ROW_PX }) ?? 2;
  const pointsText = entry.hasList ? (tournamentStarted ? String(entry.currentScore) : '0') : '—';

  const nameNode =
    isModel && onModelClick ? (
      <Link
        fontSize='sm'
        noOfLines={1}
        textAlign='left'
        minW={0}
        color={aiNameColor}
        fontWeight='medium'
        onClick={() => onModelClick(entry.id as AiModelId)}
        _hover={{ textDecoration: 'underline' }}
      >
        {entry.name}
      </Link>
    ) : (
      <Text fontSize='sm' noOfLines={1} textAlign='left' minW={0} fontWeight={isYou ? 'semibold' : undefined}>
        {entry.name}
      </Text>
    );

  return (
    <Grid
      templateColumns={columns}
      columnGap={2}
      alignItems='center'
      py={LEADERBOARD_ROW_PY}
      px={rowPx}
      borderRadius='md'
      background='listItemBackground'
      border={isYou ? LEADERBOARD_YOU_BORDER : undefined}
      borderColor={isYou ? LEADERBOARD_YOU_BORDER_COLOR : undefined}
      minH={LEADERBOARD_ROW_MIN_H}
      w='full'
    >
      <Text
        fontSize='sm'
        fontWeight='medium'
        textAlign='left'
        whiteSpace='nowrap'
        flexShrink={0}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {entry.hasList ? entry.displayRank : '—'}
      </Text>

      <Box display='flex' justifyContent='center' alignItems='center' w='1.25rem' flexShrink={0}>
        {entry.hasList && entry.showRankChange ? (
          <RankChangeIndicator memberData={member} getListPosition={() => listPosition} fontSize='xs' />
        ) : null}
      </Box>

      {nameNode}

      <HStack spacing={1} justify='flex-end' flexWrap='wrap' minW={0} />

      <Text
        fontSize='sm'
        textAlign='right'
        flexShrink={0}
        sx={{ fontVariantNumeric: 'tabular-nums' }}
        lineHeight='short'
      >
        {pointsText}
      </Text>
    </Grid>
  );
}

export function AiLeagueCompareCtaRow(props: { isLoggedIn: boolean }) {
  const columns =
    useBreakpointValue({
      base: LEADERBOARD_ROW_COLUMNS_COMPACT,
      sm: LEADERBOARD_ROW_COLUMNS,
    }) ?? LEADERBOARD_ROW_COLUMNS_COMPACT;
  const rowPx = useBreakpointValue({ base: 2, sm: LEADERBOARD_ROW_PX }) ?? 2;

  return (
    <Grid
      templateColumns={columns}
      columnGap={2}
      alignItems='center'
      py={LEADERBOARD_ROW_PY}
      px={rowPx}
      borderRadius='md'
      background='listItemBackground'
      border={LEADERBOARD_YOU_BORDER}
      borderColor={LEADERBOARD_YOU_BORDER_COLOR}
      minH={LEADERBOARD_ROW_MIN_H}
      w='full'
    >
      <Text fontSize='sm' fontWeight='medium'>
        —
      </Text>
      <Box w='1.25rem' />
      <Text fontSize='sm' color='gray.500' gridColumn='3 / -1'>
        {props.isLoggedIn ? (
          'Pick your teams to compare'
        ) : (
          <>
            <Link as={RouterLink} to='/login' color='blue.500'>
              Log in
            </Link>
            {' and pick your teams to compare'}
          </>
        )}
      </Text>
    </Grid>
  );
}

export function AiLeagueLeaderboard(props: {
  entries: AiLeagueLeaderboardEntry[];
  getMemberForRow: (entry: AiLeagueLeaderboardEntry) => leagueMember;
  tournamentStarted: boolean;
  showCompareCta: boolean;
  isLoggedIn: boolean;
  onModelClick?: (modelId: AiModelId) => void;
}) {
  const { entries, getMemberForRow, tournamentStarted, showCompareCta, isLoggedIn, onModelClick } = props;
  const rankedEntries = entries.filter((entry) => entry.id !== 'you' || entry.hasList);

  if (rankedEntries.length === 0 && !showCompareCta) {
    return (
      <Center py={8} fontSize='sm' color='gray.500'>
        No entries yet
      </Center>
    );
  }

  return (
    <Box w='full'>
      {rankedEntries.map((entry, index) => (
        <Box key={entry.id} mb={1}>
          <AiLeagueLeaderboardRow
            entry={entry}
            member={getMemberForRow(entry)}
            listPosition={index}
            tournamentStarted={tournamentStarted}
            onModelClick={onModelClick}
          />
        </Box>
      ))}
      {showCompareCta ? (
        <Box mt={1}>
          <AiLeagueCompareCtaRow isLoggedIn={isLoggedIn} />
        </Box>
      ) : null}
    </Box>
  );
}
